import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Navbar from '../components/Navbar';

function EventDetails() {
  const { eventId } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('details'); // details, attendees, invite
  const [attendeeFilter, setAttendeeFilter] = useState('all');
  const [connections, setConnections] = useState([]);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchEventDetails();
    fetchAttendees();
  }, [eventId]);

  useEffect(() => {
    if (activeTab === 'invite') {
      fetchConnections();
    }
  }, [activeTab]);

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvent(response.data.event);
    } catch (error) {
      console.error('Error fetching event:', error);
      setMessage('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (status = null) => {
    try {
      const token = localStorage.getItem('accessToken');
      const url = status ? `/api/events/${eventId}/attendees?status=${status}` : `/api/events/${eventId}/attendees`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendees(response.data.attendees);
    } catch (error) {
      console.error('Error fetching attendees:', error);
    }
  };

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/connections', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out users who already RSVP'd
      const attendeeIds = attendees.map(a => a.user_id);
      const availableConnections = response.data.connections.filter(
        conn => !attendeeIds.includes(conn.connected_user_id)
      );
      
      setConnections(availableConnections);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const handleInvite = async (userId) => {
    setInviting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`/api/events/${eventId}/invite`, 
        { userId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setMessage('✅ Invite sent successfully!');
      // Remove invited user from list
      setConnections(connections.filter(c => c.connected_user_id !== userId));
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRSVP = async (status) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`/api/events/${eventId}/rsvp`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setMessage(`✅ RSVP updated to "${status}"!`);
      fetchEventDetails();
      fetchAttendees();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to update RSVP');
    }
  };

  const handleCancelEvent = async () => {
    if (!window.confirm('Are you sure you want to cancel this event? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      navigate('/events');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to cancel event');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isUpcoming = (dateString) => {
    return new Date(dateString) >= new Date();
  };

  useEffect(() => {
    if (attendeeFilter === 'all') {
      fetchAttendees();
    } else {
      fetchAttendees(attendeeFilter);
    }
  }, [attendeeFilter]);

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div className="card">Loading...</div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div className="card">
            <p>Event not found</p>
            <button onClick={() => navigate('/events')} className="btn btn-primary">
              Back to Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isCreator = event.created_by === parseInt(localStorage.getItem('userId'));
  const canRSVP = isUpcoming(event.event_date) && !event.is_cancelled;

  return (
    <div>
      <Navbar />

      <div className="container">
        <div className="card">
          <button 
            onClick={() => navigate('/events')}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              cursor: 'pointer',
              fontSize: '16px',
              marginBottom: '16px'
            }}
          >
            ← Back to Events
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <h2>{event.title}</h2>
                
                {event.is_cancelled && (
                  <span style={{
                    padding: '4px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Cancelled
                  </span>
                )}
                
                {event.group_name && (
                  <span style={{
                    padding: '4px 12px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    📁 {event.group_name}
                  </span>
                )}
                
                {event.target_identity && (
                  <span style={{
                    padding: '4px 12px',
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {event.target_identity.icon} {event.target_identity.category_display}
                  </span>
                )}
              </div>
              
              <div style={{ marginTop: '16px', fontSize: '16px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <strong>📅 When:</strong> {formatDate(event.event_date)}
                  {event.end_date && ` - ${formatDate(event.end_date)}`}
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <strong>
                    {event.location_type === 'online' ? '💻' : event.location_type === 'hybrid' ? '🌐' : '📍'} 
                    {' '}Where:
                  </strong>
                  {' '}
                  {event.location_type === 'online' && 'Online Event'}
                  {event.location_type === 'physical' && (
                    event.location_city ? `${event.location_city}, ${event.location_state}` : 'Location TBD'
                  )}
                  {event.location_type === 'hybrid' && 'Hybrid (In-person & Online)'}
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <strong>👥 Attendees:</strong> {parseInt(event.going_count) || 0} going, {parseInt(event.interested_count) || 0} interested
                  {event.max_attendees && ` (max ${event.max_attendees})`}
                </div>
                
                <div>
                  <strong>👤 Organizer:</strong> {event.creator_first_name} {event.creator_last_name}
                </div>
              </div>
            </div>

            {isCreator && canRSVP && (
              <button 
                onClick={handleCancelEvent}
                className="btn btn-secondary"
                style={{ backgroundColor: '#dc3545', color: 'white' }}
              >
                Cancel Event
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '24px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setActiveTab('details')}
              className={activeTab === 'details' ? 'btn btn-primary' : 'btn btn-secondary'}
            >
              Details
            </button>
            <button 
              onClick={() => setActiveTab('attendees')}
              className={activeTab === 'attendees' ? 'btn btn-primary' : 'btn btn-secondary'}
            >
              Attendees ({(parseInt(event.going_count) || 0) + (parseInt(event.interested_count) || 0)})
            </button>
            {(isCreator || event.my_rsvp_status) && (
              <button 
                onClick={() => setActiveTab('invite')}
                className={activeTab === 'invite' ? 'btn btn-primary' : 'btn btn-secondary'}
              >
                Invite
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className="card" style={{
            backgroundColor: message.includes('✅') || message.includes('success') ? '#d4edda' : '#f8d7da',
            color: message.includes('✅') || message.includes('success') ? '#155724' : '#721c24'
          }}>
            {message}
          </div>
        )}

        {activeTab === 'details' ? (
          <div className="card">
            <h3>About This Event</h3>
            {event.description ? (
              <p style={{ marginTop: '12px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {event.description}
              </p>
            ) : (
              <p style={{ marginTop: '12px', color: '#999' }}>
                No description provided.
              </p>
            )}

            {/* Location Details */}
            {(event.my_rsvp_status === 'going' || event.my_rsvp_status === 'interested') && (
              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #eee' }}>
                <h4>Location Details</h4>
                
                {event.location_address && (
                  <div style={{ marginTop: '12px' }}>
                    <strong>Address:</strong>
                    <p style={{ marginTop: '4px' }}>{event.location_address}</p>
                    <p>{event.location_city}, {event.location_state}</p>
                  </div>
                )}
                
                {event.online_link && (
                  <div style={{ marginTop: '12px' }}>
                    <strong>Online Link:</strong>
                    <p style={{ marginTop: '4px' }}>
                      <a href={event.online_link} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                        {event.online_link}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* RSVP Section */}
            {canRSVP && (
              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #eee' }}>
                <h4>Will you attend?</h4>
                <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => handleRSVP('going')}
                    className="btn"
                    style={{
                      backgroundColor: event.my_rsvp_status === 'going' ? '#28a745' : '#e0e0e0',
                      color: event.my_rsvp_status === 'going' ? 'white' : '#333'
                    }}
                  >
                    ✓ Going
                  </button>
                  <button 
                    onClick={() => handleRSVP('interested')}
                    className="btn"
                    style={{
                      backgroundColor: event.my_rsvp_status === 'interested' ? '#ffc107' : '#e0e0e0',
                      color: event.my_rsvp_status === 'interested' ? '#000' : '#333'
                    }}
                  >
                    ★ Interested
                  </button>
                  <button 
                    onClick={() => handleRSVP('not_going')}
                    className="btn"
                    style={{
                      backgroundColor: event.my_rsvp_status === 'not_going' ? '#dc3545' : '#e0e0e0',
                      color: event.my_rsvp_status === 'not_going' ? 'white' : '#333'
                    }}
                  >
                    ✗ Can't Go
                  </button>
                </div>
                {!event.my_rsvp_status && (
                  <p style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
                    💡 RSVP to see full location details
                  </p>
                )}
              </div>
            )}
          </div>
        ) : activeTab === 'attendees' ? (
          <div>
            <div className="card">
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => setAttendeeFilter('all')}
                  className={attendeeFilter === 'all' ? 'btn btn-primary' : 'btn btn-secondary'}
                >
                  All ({(parseInt(event.going_count) || 0) + (parseInt(event.interested_count) || 0)})
                </button>
                <button 
                  onClick={() => setAttendeeFilter('going')}
                  className={attendeeFilter === 'going' ? 'btn btn-primary' : 'btn btn-secondary'}
                >
                  Going ({parseInt(event.going_count) || 0})
                </button>
                <button 
                  onClick={() => setAttendeeFilter('interested')}
                  className={attendeeFilter === 'interested' ? 'btn btn-primary' : 'btn btn-secondary'}
                >
                  Interested ({parseInt(event.interested_count) || 0})
                </button>
              </div>
            </div>

            {attendees.length === 0 ? (
              <div className="card">
                <p>No attendees yet.</p>
              </div>
            ) : (
              attendees.map((attendee) => (
                <div key={attendee.user_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      borderRadius: '50%', 
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px'
                    }}>
                      {attendee.photo_url ? '📷' : '👤'}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{attendee.first_name} {attendee.last_name}</strong>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: attendee.status === 'going' ? '#28a745' : '#ffc107',
                          color: attendee.status === 'going' ? 'white' : '#000',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {attendee.status === 'going' ? '✓ Going' : '★ Interested'}
                        </span>
                      </div>
                      <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                        📍 {attendee.current_city}, {attendee.current_state}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : activeTab === 'invite' ? (
          // Invite Tab
          <div>
            <div className="card">
              <h3>Invite Your Connections</h3>
              <p style={{ marginTop: '8px', color: '#666' }}>
                Invite people from your connections to this event
              </p>
            </div>

            {connections.length === 0 ? (
              <div className="card">
                <p>No connections available to invite.</p>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  All your connections have already RSVP'd to this event, or you don't have any connections yet.
                </p>
              </div>
            ) : (
              connections.map((conn) => (
                <div key={conn.connected_user_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      borderRadius: '50%', 
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px'
                    }}>
                      {conn.photo_url ? '📷' : '👤'}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <strong>
                        {conn.disclosure_level >= 1 
                          ? `${conn.first_name} ${conn.disclosure_level >= 2 ? conn.last_name : ''}`
                          : 'Anonymous User'}
                      </strong>
                      <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                        📍 {conn.current_city}, {conn.current_state}
                      </p>
                    </div>
                    
                    <button 
                      onClick={() => handleInvite(conn.connected_user_id)}
                      disabled={inviting}
                      className="btn btn-primary"
                    >
                      {inviting ? 'Sending...' : 'Invite'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          // Attendees Tab
          <div>
            <div className="card">
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => setAttendeeFilter('all')}
                  className={attendeeFilter === 'all' ? 'btn btn-primary' : 'btn btn-secondary'}
                >
                  All ({(parseInt(event.going_count) || 0) + (parseInt(event.interested_count) || 0)})
                </button>
                <button 
                  onClick={() => setAttendeeFilter('going')}
                  className={attendeeFilter === 'going' ? 'btn btn-primary' : 'btn btn-secondary'}
                >
                  Going ({parseInt(event.going_count) || 0})
                </button>
                <button 
                  onClick={() => setAttendeeFilter('interested')}
                  className={attendeeFilter === 'interested' ? 'btn btn-primary' : 'btn btn-secondary'}
                >
                  Interested ({parseInt(event.interested_count) || 0})
                </button>
              </div>
            </div>

            {attendees.length === 0 ? (
              <div className="card">
                <p>No attendees yet.</p>
              </div>
            ) : (
              attendees.map((attendee) => (
                <div key={attendee.user_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      borderRadius: '50%', 
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px'
                    }}>
                      {attendee.photo_url ? '📷' : '👤'}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{attendee.first_name} {attendee.last_name}</strong>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: attendee.status === 'going' ? '#28a745' : '#ffc107',
                          color: attendee.status === 'going' ? 'white' : '#000',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {attendee.status === 'going' ? '✓ Going' : '★ Interested'}
                        </span>
                      </div>
                      <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                        📍 {attendee.current_city}, {attendee.current_state}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EventDetails;

